import React from 'react';

interface ServiceCard {
  title: string;
  bullets: string[];
  icon: string;
}

interface ServicesSectionProps {
  title?: string;
  subtitle?: string;
  services?: ServiceCard[];
}

const defaultServices: ServiceCard[] = [
  {
    title: "WEB SERVICES",
    bullets: [
      "Custom website development",
      "E-commerce solutions",
      "Mobile-responsive design"
    ],
    icon: "💻"
  },
  {
    title: "DIGITAL MARKETING",
    bullets: [
      "SEO optimization",
      "Social media management",
      "Content marketing strategy"
    ],
    icon: "📈"
  },
  {
    title: "MEDIA SERVICES",
    bullets: [
      "Video production",
      "Graphic design",
      "Brand identity development"
    ],
    icon: "🎨"
  },
  {
    title: "ENTERPRISE APPLICATION",
    bullets: [
      "Custom software development",
      "Database management",
      "Cloud integration solutions"
    ],
    icon: "⚡"
  }
];

export default function ServicesSection({ 
  title = "Comprehensive Web Development & Digital Marketing Services",
  subtitle = "We provide end-to-end digital solutions to help your business grow online with cutting-edge technology and proven strategies.",
  services = defaultServices
}: ServicesSectionProps) {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-gray-50 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 hover:border-blue-200"
            >
              {/* Icon */}
              <div className="text-4xl mb-6 group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors duration-300">
                {service.title}
              </h3>
              
              {/* Bullet Points */}
              <ul className="space-y-3">
                {service.bullets.map((bullet, bulletIndex) => (
                  <li 
                    key={bulletIndex}
                    className="flex items-start text-gray-600 group-hover:text-gray-800 transition-colors duration-300"
                  >
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0 group-hover:bg-blue-600 transition-colors duration-300"></div>
                    <span className="text-sm leading-relaxed">{bullet}</span>
                  </li>
                ))}
              </ul>
              
              {/* Hover Effect Indicator */}
              <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-2 text-gray-500 group cursor-pointer">
            <span className="text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
              Explore all services
            </span>
            <svg 
              className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
